import { useState, useEffect } from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { User } from '../types';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: Error | null;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser: FirebaseUser | null) => {
        if (firebaseUser) {
          try {
            // Fetch user document from Firestore
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

            if (userDoc.exists()) {
              const userData = userDoc.data() as User;
              setAuthState({
                user: {
                  ...userData,
                  id: firebaseUser.uid,
                },
                loading: false,
                error: null,
              });
            } else {
              // User document doesn't exist
              setAuthState({
                user: null,
                loading: false,
                error: new Error('User document not found'),
              });
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
            setAuthState({
              user: null,
              loading: false,
              error: error as Error,
            });
          }
        } else {
          // User is not authenticated
          setAuthState({
            user: null,
            loading: false,
            error: null,
          });
        }
      },
      (error) => {
        console.error('Auth state change error:', error);
        setAuthState({
          user: null,
          loading: false,
          error: error as Error,
        });
      }
    );

    return () => unsubscribe();
  }, []);

  return authState;
};
